'use client'

import { useState } from 'react'

import { useLevelProfileSnapshot } from '@/service/analytics/level-profiles.hook'
import type {
  LevelProfileAnalyticsType,
  LevelProfileFor,
} from '@/service/analytics/level-profiles.types'
import { useLevelProfileStream } from '@/service/ws/use-level-profile-stream'
import type { Exchange } from '@/types/generated/common/types'

export type LevelProfileViewState<T> =
  | { status: 'loading' }
  | { status: 'error'; error: unknown }
  | { status: 'not-published' }
  | { status: 'ready'; profile: T }

// Live-only (no history, no range query, no pan/zoom re-fetch) — a one-shot snapshot for the
// initial paint, then AnalyticsStream pushes take over. Stream wins whenever it has ever fired,
// regardless of what the snapshot query says: a push before the snapshot resolves, or after the
// snapshot resolved `not-published`, goes straight to `ready`.
export function useLevelProfile<T extends LevelProfileAnalyticsType>(
  analyticsType: T,
  exchange: Exchange,
  symbol: string,
  enabled: boolean
): LevelProfileViewState<LevelProfileFor<T>> {
  type Profile = LevelProfileFor<T>
  const [streamProfile, setStreamProfile] = useState<Profile>()

  // Must clear stale stream state on symbol/exchange/analyticsType change, or switching charts
  // briefly shows the previous symbol's levels while the new symbol's snapshot/stream are still
  // in flight. Adjusted during render (React's documented pattern for this) instead of in a
  // useEffect: an effect-based reset still paints one frame with the stale profile before it
  // fires, which is exactly the flash this is guarding against.
  const [prevParams, setPrevParams] = useState({ exchange, symbol, analyticsType })
  if (
    prevParams.exchange !== exchange ||
    prevParams.symbol !== symbol ||
    prevParams.analyticsType !== analyticsType
  ) {
    setPrevParams({ exchange, symbol, analyticsType })
    setStreamProfile(undefined)
  }

  const params = { exchange, symbol, analyticsType }
  const snapshotQuery = useLevelProfileSnapshot(params, enabled)

  useLevelProfileStream({
    connected: enabled,
    payload: params,
    onMessage: (profile) => setStreamProfile(profile as Profile),
  })

  if (streamProfile) return { status: 'ready', profile: streamProfile }
  if (snapshotQuery.isError) return { status: 'error', error: snapshotQuery.error }
  if (snapshotQuery.data?.status === 'not-published') return { status: 'not-published' }
  if (snapshotQuery.data?.status === 'published') {
    return { status: 'ready', profile: snapshotQuery.data.profile }
  }
  return { status: 'loading' }
}
